import { useState, useEffect, type FormEvent } from 'react';
import { useAnimatedNavigate } from '../hooks/useAnimatedNavigate';
import { useAuth } from '../hooks/useAuth';
import { useMsalLogin } from '../auth/useMsalLogin';
import { CadeAvatar } from '../components/CadeAvatar';
import { trpc } from '../api/trpc';

const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

export function LoginPage() {
  const { login, isLoggingIn, loginError, isAuthenticated, ssoError: ssoRedirectError } = useAuth();
  const { loginWithRedirect, isLoading: ssoLoading, error: ssoError, isSsoAvailable } = useMsalLogin();
  const navigate = useAnimatedNavigate();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showOktaForm, setShowOktaForm] = useState(false);
  const [oktaEmail, setOktaEmail] = useState('');
  const [oktaError, setOktaError] = useState('');
  const [oktaSubmitting, setOktaSubmitting] = useState(false);
  const discoverOktaSso = trpc.auth.discoverOktaSso.useMutation();

  useEffect(() => {
    if (isAuthenticated) {
      navigate('/', { replace: true });
    }
  }, [isAuthenticated, navigate]);

  async function handleSubmit(e: FormEvent) {
    e.preventDefault();
    try {
      const result = await login(email, password);
      if (result.kind === 'challenge') {
        // MFA enforcement (Part 2 §13.6) — pass the challengeToken to
        // /login/mfa via Router state so the URL doesn't carry it.
        navigate('/login/mfa', { state: { challengeToken: result.challengeToken } });
        return;
      }
      navigate('/', { replace: true });
    } catch {
      // error shown via loginError
    }
  }

  return (
    <div className="login-container">
      <div className="login-card">
        <div className="login-logo" style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 14 }}>
          <CadeAvatar size={80} disc />
          <span className="login-logo-text">Cade</span>
        </div>
        {isSsoAvailable && (
          <>
            <button
              className="btn-sso"
              onClick={() => { void loginWithRedirect(); }}
              disabled={ssoLoading}
            >
              <svg width="16" height="16" viewBox="0 0 21 21" fill="none" xmlns="http://www.w3.org/2000/svg">
                <rect width="10" height="10" fill="#f25022"/>
                <rect x="11" width="10" height="10" fill="#7fba00"/>
                <rect y="11" width="10" height="10" fill="#00a4ef"/>
                <rect x="11" y="11" width="10" height="10" fill="#ffb900"/>
              </svg>
              {ssoLoading ? 'Signing in...' : 'Sign in with Microsoft'}
            </button>
            {(ssoError || ssoRedirectError) && <p className="login-error">{ssoError || ssoRedirectError}</p>}
          </>
        )}
        {!showOktaForm ? (
          <button
            className="btn-sso"
            onClick={() => setShowOktaForm(true)}
          >
            <svg width="16" height="16" viewBox="0 0 100 100" fill="none" xmlns="http://www.w3.org/2000/svg">
              <circle cx="50" cy="50" r="50" fill="#007dc1"/>
              <circle cx="50" cy="50" r="20" fill="#fff"/>
            </svg>
            Sign in with Okta
          </button>
        ) : (
          <form
            className="okta-slug-form"
            onSubmit={(e) => {
              e.preventDefault();
              setOktaError('');
              const email = oktaEmail.trim();
              if (!email || !EMAIL_REGEX.test(email)) {
                setOktaError('Enter your work email');
                return;
              }
              setOktaSubmitting(true);
              void discoverOktaSso
                .mutateAsync({ email })
                .then((result) => {
                  if (!result.issuer) {
                    setOktaError(
                      "We don't recognize that domain. Sign in from your Okta dashboard tile, or contact your admin.",
                    );
                    setOktaSubmitting(false);
                    return;
                  }
                  window.location.href = `/auth/okta/login?iss=${encodeURIComponent(result.issuer)}`;
                })
                .catch(() => {
                  setOktaError('Could not look up your organization. Try again or contact support.');
                  setOktaSubmitting(false);
                });
            }}
          >
            <div className="okta-slug-input-group">
              <input
                type="email"
                value={oktaEmail}
                onChange={(e) => { setOktaEmail(e.target.value); setOktaError(''); }}
                placeholder="you@your-org.com"
                autoFocus
                autoComplete="email"
                className="okta-slug-input"
                disabled={oktaSubmitting}
              />
              <button type="submit" className="btn-sso okta-slug-submit" disabled={oktaSubmitting}>
                <svg width="16" height="16" viewBox="0 0 100 100" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <circle cx="50" cy="50" r="50" fill="#007dc1"/>
                  <circle cx="50" cy="50" r="20" fill="#fff"/>
                </svg>
                {oktaSubmitting ? 'Looking up…' : 'Continue with Okta'}
              </button>
            </div>
            {oktaError && <p className="login-error">{oktaError}</p>}
            <button type="button" className="btn-link" onClick={() => { setShowOktaForm(false); setOktaError(''); setOktaSubmitting(false); }}>
              Back
            </button>
          </form>
        )}
        <div className="login-divider">
          <span>or</span>
        </div>
        <form onSubmit={(e) => { void handleSubmit(e); }} className="login-form">
          <div className="form-field">
            <label htmlFor="email">Email</label>
            <input
              id="email"
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
              autoComplete="email"
              placeholder="analyst@company.com"
            />
          </div>
          <div className="form-field">
            <label htmlFor="password">Password</label>
            <input
              id="password"
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              required
              autoComplete="current-password"
              placeholder="••••••••"
            />
          </div>
          {loginError && <p className="login-error">{loginError}</p>}
          <button type="submit" disabled={isLoggingIn} className="btn-recommended">
            {isLoggingIn ? 'Signing in…' : 'Sign in'}
          </button>
        </form>
      </div>
    </div>
  );
}
